/**
 * CompanyDashboard.jsx - Bedriftens hovedoversikt
 * 
 * Viser:
 * - Statistikk-kort (aktive moduler, dokumenter, frister, brukere)
 * - Hurtighandlinger (dynamisk konfigurerbar for admin)
 * - Nylig aktivitet
 */

import React, { useState, useEffect, useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useCompany } from '../context/CompanyContext';

// Ruting for aktive moduler med dedikerte dashboards
const MODULE_DASHBOARD_ROUTES = {
  'barnehage': '/company/dashboard/barnehage',
};

function getModuleRoute(moduleKey) {
  return MODULE_DASHBOARD_ROUTES[moduleKey] || `/company/modules/${moduleKey}`;
}

const MODULE_ICON_MAP = {
  'arbeidsrett': Briefcase, 'arbeidsrett-bedrift': Briefcase,
  'husleierett': Home, 'forbrukerrett': ShoppingBag,
  'barnevern': Shield, 'barnevern-master': Shield,
  'barnehage': Users, 'skole': Users,
  'gdpr': Shield, 'kontraktsbibliotek': FileSignature,
  'hms': Shield,
};
import { 
  FileText, 
  Clock, 
  Users,
  ChevronRight,
  Layers,
  Briefcase,
  Home,
  ShoppingBag,
  Shield,
  Plus,
  AlertTriangle,
  Settings,
  X,
  Check,
  UserX,
  Zap,
  ScrollText,
  Calculator,
  FileSignature,
  Search,
  Scale,
  Baby,
  Monitor,
  Lock,
  HardDrive,
  WifiOff,
  Key,
  Server
} from 'lucide-react';

// Alle tilgjengelige hurtighandlinger fra modulene
const ALL_QUICK_ACTIONS = [
  // Arbeidsrett
  { id: 'ny-kontrakt', label: 'Ny kontrakt', icon: FileSignature, path: '/company/modules/arbeidsrett', color: '#B87333', module: 'arbeidsrett' },
  { id: 'oppsigelse', label: 'Oppsigelse', icon: UserX, path: '/company/modules/arbeidsrett', color: '#ef4444', module: 'arbeidsrett' },
  { id: 'permittering', label: 'Permittering', icon: Zap, path: '/company/modules/arbeidsrett', color: '#f59e0b', module: 'arbeidsrett' },
  { id: 'analyser-kontrakt', label: 'Analyser kontrakt', icon: ScrollText, path: '/company/modules/arbeidsrett', color: '#6366f1', module: 'arbeidsrett' },
  { id: 'saerlig-uavhengig', label: 'Særlig uavhengig', icon: Calculator, path: '/company/modules/arbeidsrett', color: '#a855f7', module: 'arbeidsrett' },
  
  // Husleierett
  { id: 'leiekontrakt', label: 'Leiekontrakt', icon: Home, path: '/company/modules/husleierett', color: '#5B8BA0', module: 'husleierett' },
  { id: 'depositum', label: 'Depositum', icon: Shield, path: '/company/modules/husleierett', color: '#22c55e', module: 'husleierett' },
  { id: 'oppsigelse-leie', label: 'Oppsigelse leie', icon: AlertTriangle, path: '/company/modules/husleierett', color: '#f59e0b', module: 'husleierett' },
  
  // Forbrukerrett
  { id: 'reklamasjon', label: 'Reklamasjon', icon: ShoppingBag, path: '/company/modules/forbrukerrett', color: '#22c55e', module: 'forbrukerrett' },
  { id: 'angrerett', label: 'Angrerett', icon: Scale, path: '/company/modules/forbrukerrett', color: '#3b82f6', module: 'forbrukerrett' },
  
  // Barnevern
  { id: 'barnevern-sak', label: 'Barnevernssak', icon: Baby, path: '/company/modules/barnevern', color: '#ec4899', module: 'barnevern' },
];

// Standard hurtighandlinger (brukes som default)
const DEFAULT_QUICK_ACTION_IDS = ['ny-kontrakt', 'oppsigelse', 'leiekontrakt', 'reklamasjon'];

// LocalStorage-nøkkel for bedriftens hurtighandlinger
const QUICK_ACTIONS_STORAGE_KEY = 'rettwerk_company_quick_actions';

// Hent lagrede hurtighandlinger
const getStoredQuickActions = () => {
  try {
    const stored = localStorage.getItem(QUICK_ACTIONS_STORAGE_KEY);
    if (stored) {
      const parsed = JSON.parse(stored);
      if (Array.isArray(parsed) && parsed.length > 0) {
        return parsed;
      }
    }
  } catch (e) {
    console.error('Error reading quick actions from localStorage:', e);
  }
  return DEFAULT_QUICK_ACTION_IDS;
};

// Lagre hurtighandlinger
const saveQuickActions = (actionIds) => {
  try {
    localStorage.setItem(QUICK_ACTIONS_STORAGE_KEY, JSON.stringify(actionIds));
  } catch (e) {
    console.error('Error saving quick actions to localStorage:', e);
  }
};

// Nylig aktivitet er nå i sidebaren (CompanySidebar.jsx)

// ============================================================
// HURTIGHANDLINGER REDIGERINGSMODAL
// ============================================================
function QuickActionsEditModal({ isOpen, onClose, selectedIds, onSave }) {
  const [tempSelection, setTempSelection] = useState(selectedIds);
  const [searchTerm, setSearchTerm] = useState('');
  
  useEffect(() => {
    setTempSelection(selectedIds);
  }, [selectedIds, isOpen]);
  
  const filteredActions = useMemo(() => {
    if (!searchTerm) return ALL_QUICK_ACTIONS;
    return ALL_QUICK_ACTIONS.filter(action => 
      action.label.toLowerCase().includes(searchTerm.toLowerCase()) ||
      action.module.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);
  
  const toggleAction = (actionId) => {
    setTempSelection(prev => {
      if (prev.includes(actionId)) {
        return prev.filter(id => id !== actionId);
      }
      if (prev.length >= 4) {
        // Fjern første og legg til ny
        return [...prev.slice(1), actionId];
      }
      return [...prev, actionId];
    });
  };
  
  const handleSave = () => {
    onSave(tempSelection);
    onClose();
  };
  
  if (!isOpen) return null;
  
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/60 backdrop-blur-sm"
        onClick={onClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-lg mx-4 bg-[#1B2631] border border-[#2C3E50] rounded-2xl shadow-2xl overflow-hidden">
        {/* Header */}
        <div className="flex items-center justify-between p-5 border-b border-[#2C3E50]">
          <div>
            <h2 className="text-lg font-semibold text-white">Rediger hurtighandlinger</h2>
            <p className="text-sm text-[#8899A8] mt-0.5">Velg inntil 4 snarveier for dashbordet</p>
          </div>
          <button 
            onClick={onClose}
            className="p-2 rounded-lg hover:bg-[#2C3E50] text-[#8899A8] hover:text-white transition-colors"
          >
            <X className="w-5 h-5" />
          </button>
        </div>
        
        {/* Search */}
        <div className="p-4 border-b border-[#2C3E50]">
          <div className="relative">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#8899A8]" />
            <input
              type="text"
              placeholder="Søk etter handling..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-[#0F1419] border border-[#2C3E50] rounded-lg text-white placeholder-[#8899A8] focus:outline-none focus:border-[#B87333]"
            />
          </div>
          <p className="text-xs text-[#8899A8] mt-2">
            Valgt: {tempSelection.length}/4
          </p>
        </div>
        
        {/* Actions list */}
        <div className="p-4 max-h-80 overflow-y-auto space-y-2">
          {filteredActions.map((action) => {
            const Icon = action.icon;
            const isSelected = tempSelection.includes(action.id);
            
            return (
              <button
                key={action.id}
                onClick={() => toggleAction(action.id)}
                className={`
                  w-full flex items-center gap-3 p-3 rounded-lg transition-all
                  ${isSelected 
                    ? 'bg-[#B87333]/20 border border-[#B87333]/50' 
                    : 'bg-[#0F1419] border border-transparent hover:border-[#2C3E50]'
                  }
                `}
              >
                <div 
                  className="p-2 rounded-lg"
                  style={{ backgroundColor: `${action.color}20` }}
                >
                  <Icon className="w-4 h-4" style={{ color: action.color }} />
                </div>
                <div className="flex-1 text-left">
                  <p className="text-sm font-medium text-white">{action.label}</p>
                  <p className="text-xs text-[#8899A8] capitalize">{action.module}</p>
                </div>
                {isSelected && (
                  <div className="p-1 rounded-full bg-[#B87333]">
                    <Check className="w-3 h-3 text-white" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
        
        {/* Footer */}
        <div className="flex items-center justify-end gap-3 p-4 border-t border-[#2C3E50]">
          <button
            onClick={onClose}
            className="px-4 py-2 text-sm font-medium text-[#8899A8] hover:text-white transition-colors"
          >
            Avbryt
          </button>
          <button
            onClick={handleSave}
            disabled={tempSelection.length === 0}
            className="px-4 py-2 bg-[#B87333] hover:bg-[#A06328] disabled:opacity-50 disabled:cursor-not-allowed text-white text-sm font-medium rounded-lg transition-colors"
          >
            Lagre endringer
          </button>
        </div>
      </div>
    </div>
  );
}

// ============================================================
// HOVEDKOMPONENT
// ============================================================
export default function CompanyDashboard() {
  const { isAdmin, activeModules } = useCompany();
  
  const [quickActionIds, setQuickActionIds] = useState(getStoredQuickActions);
  const [isEditModalOpen, setIsEditModalOpen] = useState(false);
  
  // Hent de faktiske hurtighandling-objektene basert på lagrede IDs
  const quickActions = useMemo(() => {
    return quickActionIds
      .map(id => ALL_QUICK_ACTIONS.find(a => a.id === id))
      .filter(Boolean);
  }, [quickActionIds]);
  
  // Lagre endringer
  const handleSaveQuickActions = (newIds) => {
    setQuickActionIds(newIds);
    saveQuickActions(newIds);
  };

  return (
    <div className="p-6 space-y-4" data-testid="company-dashboard">
      {/* Header med Se alle moduler-knapp */}
      <div className="flex items-center justify-between">
        <h1 className="text-lg font-bold text-white">Dashbord</h1>
        <Link
          to="/company/modules"
          className="flex items-center gap-2 px-4 py-2 bg-[#dc2626] hover:bg-[#b91c1c] text-white text-sm font-medium rounded-lg transition-colors shadow-lg shadow-red-900/20"
          data-testid="see-all-modules-btn"
        >
          <Layers className="w-4 h-4" />
          Se alle moduler
        </Link>
      </div>

      {/* Aktive moduler — direkte øverst, uten overskrift */}
      {activeModules.length > 0 ? (
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {activeModules.map((mod) => {
            const Icon = MODULE_ICON_MAP[mod.key] || Layers;
            return (
              <Link
                key={mod.key}
                to={getModuleRoute(mod.key)}
                className="flex items-center gap-3 p-4 bg-[#1B2631] border border-[#2C3E50] rounded-xl hover:bg-[#B87333]/10 transition-colors group hover:border-[#B87333]/30"
                data-testid={`active-module-${mod.key}`}
              >
                <div className="p-2.5 rounded-lg bg-[#B87333]/10">
                  <Icon className="w-5 h-5 text-[#B87333]" />
                </div>
                <div className="flex-1 min-w-0">
                  <span className="text-sm font-medium text-white group-hover:text-[#B87333] transition-colors">{mod.name}</span>
                  <p className="text-xs text-[#8899A8] truncate">{mod.description || 'Aktiv modul'}</p>
                </div>
                <ChevronRight className="w-4 h-4 text-[#8899A8] group-hover:text-[#B87333]" />
              </Link>
            );
          })}
          <Link
            to="/company/modules"
            className="flex items-center justify-center gap-2 p-4 rounded-xl border-2 border-dashed border-[#2C3E50] hover:border-[#B87333]/50 hover:bg-[#B87333]/5 transition-colors group"
          >
            <Plus className="w-4 h-4 text-[#8899A8] group-hover:text-[#B87333]" />
            <span className="text-sm text-[#8899A8] group-hover:text-[#B87333]">Flere moduler</span>
          </Link>
        </div>
      ) : (
        <div className="bg-[#1B2631] border border-[#2C3E50] rounded-xl p-8 text-center">
          <Layers className="w-8 h-8 text-[#2C3E50] mx-auto mb-2" />
          <p className="text-sm text-[#8899A8]">Ingen aktive moduler ennå</p>
          <Link to="/company/modules" className="text-sm text-[#B87333] hover:underline mt-1 inline-block">
            Utforsk tilgjengelige moduler
          </Link>
        </div>
      )}

      {/* Kompakt statistikk + hurtighandlinger på én rad */}
      <div className="grid grid-cols-3 lg:grid-cols-6 gap-2">
        {/* Mini-stats */}
        {[
          { label: 'Dokumenter', value: '0', icon: FileText, color: '#B87333' },
          { label: 'Frister', value: '0', icon: Clock, color: '#f59e0b' },
          { label: 'Brukere', value: '1', icon: Users, color: '#6366f1' },
        ].map((stat, idx) => {
          const Icon = stat.icon;
          return (
            <div key={idx} className="flex items-center gap-2 p-2.5 bg-[#1B2631] border border-[#2C3E50] rounded-lg">
              <Icon className="w-3.5 h-3.5" style={{ color: stat.color }} />
              <span className="text-sm font-bold text-white">{stat.value}</span>
              <span className="text-xs text-[#8899A8] hidden sm:inline">{stat.label}</span>
            </div>
          );
        })}
        {/* Kompakte hurtighandlinger */}
        {quickActions.slice(0, 3).map((action) => {
          const Icon = action.icon;
          return (
            <Link
              key={action.id}
              to={action.path}
              className="flex items-center gap-2 p-2.5 bg-[#1B2631] border border-[#2C3E50] rounded-lg hover:bg-[#B87333]/10 hover:border-[#B87333]/30 transition-colors group"
              data-testid={`quick-action-${action.id}`}
            >
              <Icon className="w-3.5 h-3.5" style={{ color: action.color }} />
              <span className="text-xs text-[#8899A8] group-hover:text-white truncate">{action.label}</span>
            </Link>
          );
        })}
      </div>
      
      {/* Redigeringsmodal */}
      <QuickActionsEditModal
        isOpen={isEditModalOpen}
        onClose={() => setIsEditModalOpen(false)}
        selectedIds={quickActionIds}
        onSave={handleSaveQuickActions}
      />
      
      {/* RettVerk Desktop Teaser */}
      <div 
        className="relative overflow-hidden bg-gradient-to-br from-[#1B2631] to-[#0F1419] border border-[#2C3E50] rounded-xl p-6"
        data-testid="rettwerk-desktop-teaser"
      >
        {/* Background pattern */}
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-0 right-0 w-64 h-64 bg-[#B87333] rounded-full blur-3xl" />
          <div className="absolute bottom-0 left-0 w-48 h-48 bg-[#22c55e] rounded-full blur-3xl" />
        </div>
        
        <div className="relative z-10">
          {/* Header med badge */}
          <div className="flex items-start justify-between mb-6">
            <div className="flex items-center gap-4">
              <div className="p-3 rounded-xl bg-gradient-to-br from-[#B87333] to-[#8B5A2B] shadow-lg">
                <Monitor className="w-8 h-8 text-white" />
              </div>
              <div>
                <div className="flex items-center gap-2 mb-1">
                  <h2 className="text-xl font-bold text-white">RettVerk Desktop</h2>
                  <span className="px-2 py-0.5 bg-[#B87333]/20 text-[#B87333] text-xs font-bold rounded flex items-center gap-1">
                    <Lock className="w-3 h-3" />
                    KOMMER SNART
                  </span>
                </div>
                <p className="text-sm text-[#8899A8]">
                  Nedlastbart program med lokal datalagring og militærgrad-kryptering
                </p>
              </div>
            </div>
          </div>
          
          {/* Sikkerhetskontroll-boks */}
          <div className="p-4 bg-[#0F1419] border border-[#22c55e]/30 rounded-xl mb-6">
            <div className="flex items-start gap-3">
              <div className="p-2 rounded-lg bg-[#22c55e]/20">
                <Shield className="w-5 h-5 text-[#22c55e]" />
              </div>
              <div>
                <h3 className="text-sm font-semibold text-white flex items-center gap-2">
                  Sikkerhetskontroll
                  <span className="px-1.5 py-0.5 bg-[#22c55e]/20 text-[#22c55e] text-[10px] font-bold rounded">
                    AES-256
                  </span>
                </h3>
                <p className="text-sm text-[#8899A8] mt-1 leading-relaxed">
                  <span className="text-white font-medium">Din bedrift eier nøkkelen.</span> RettVerk Desktop 
                  vil tilby militærgrad-kryptering og automatisk serverbackup direkte fra din maskin.
                </p>
              </div>
            </div>
          </div>
          
          {/* Tre hovedfunksjoner */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
            {/* Hardware-binding */}
            <div className="p-4 bg-[#0F1419]/80 border border-[#2C3E50] rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-[#B87333]/20">
                  <Key className="w-4 h-4 text-[#B87333]" />
                </div>
                <h4 className="text-sm font-semibold text-white">Hardware-binding</h4>
              </div>
              <p className="text-xs text-[#8899A8] leading-relaxed">
                Krypteringsnøkkelen låses til maskinens UUID – biblioteket kan ikke flyttes uten autorisasjon.
              </p>
            </div>
            
            {/* Server-sync */}
            <div className="p-4 bg-[#0F1419]/80 border border-[#2C3E50] rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-[#3b82f6]/20">
                  <Server className="w-4 h-4 text-[#3b82f6]" />
                </div>
                <h4 className="text-sm font-semibold text-white">Dual-Write Sync</h4>
              </div>
              <p className="text-xs text-[#8899A8] leading-relaxed">
                Automatisk sanntids-speiling av krypterte filer til din bedriftsserver.
              </p>
            </div>
            
            {/* Offline-modus */}
            <div className="p-4 bg-[#0F1419]/80 border border-[#2C3E50] rounded-xl">
              <div className="flex items-center gap-3 mb-2">
                <div className="p-2 rounded-lg bg-[#22c55e]/20">
                  <WifiOff className="w-4 h-4 text-[#22c55e]" />
                </div>
                <h4 className="text-sm font-semibold text-white">100% Offline</h4>
              </div>
              <p className="text-xs text-[#8899A8] leading-relaxed">
                Generer ukeplaner og månedsplaner helt uten internettforbindelse.
              </p>
            </div>
          </div>
          
          {/* RettVerk Vault seksjon */}
          <div className="p-4 bg-gradient-to-r from-[#B87333]/10 to-[#B87333]/5 border border-[#B87333]/30 rounded-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-lg bg-[#B87333]/20">
                  <HardDrive className="w-5 h-5 text-[#B87333]" />
                </div>
                <div>
                  <h4 className="text-sm font-semibold text-white flex items-center gap-2">
                    RettVerk Vault
                    <span className="px-1.5 py-0.5 bg-[#B87333]/30 text-[#B87333] text-[10px] font-bold rounded">
                      KUN B2B
                    </span>
                  </h4>
                  <p className="text-xs text-[#8899A8]">
                    Lokal kryptert lagring med full datasuverenitet
                  </p>
                </div>
              </div>
              <button 
                className="px-4 py-2 bg-[#2C3E50] text-[#8899A8] rounded-lg text-sm font-medium cursor-not-allowed opacity-60"
                disabled
              >
                Kommer snart
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
